<template>
  <div v-if="isVisible" class="maintenance-overlay">
    <div class="maintenance-modal">
      <div class="maintenance-icon">
        <i class="fas fa-tools"></i>
      </div>
      
      <h2 class="maintenance-title">Wartungsarbeiten</h2>
      
      <p class="maintenance-message">
        Wir aktualisieren gerade unsere Anwendung im Hintergrund, um eine bessere Erfahrung zu bieten.
      </p>
      
      <p class="maintenance-submessage">
        Bitte versuche es in wenigen Minuten erneut.
      </p>
      
      <div class="loading-animation">
        <div class="spinner"></div>
        <div class="dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
      
      <div class="retry-section">
        <button @click="retryConnection" class="retry-button" :disabled="isRetrying">
          <i class="fas fa-redo" :class="{ 'spinning': isRetrying }"></i>
          {{ isRetrying ? 'Prüfe Verbindung...' : 'Verbindung prüfen' }}
        </button>
        
        <div class="countdown-section">
          <p class="countdown-text">
            Nächste Prüfung in: <span class="countdown-timer">{{ countdown }}s</span>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import maintenanceService from '../services/maintenanceService.js'

export default {
  name: 'MaintenanceModal',
  data() {
    return {
      isVisible: false,
      isRetrying: false,
      countdown: 30,
      countdownInterval: null
    }
  },
  mounted() {
    // Listener für Maintenance-Status hinzufügen
    maintenanceService.addListener(this.onMaintenanceStatusChange)
    
    // Initialen Status prüfen
    this.isVisible = maintenanceService.getMaintenanceMode()
    
    // Countdown starten wenn Modal sichtbar ist
    if (this.isVisible) {
      this.startCountdown()
    }
  },
  beforeUnmount() {
    // Listener entfernen
    maintenanceService.removeListener(this.onMaintenanceStatusChange)
    
    // Countdown stoppen
    this.stopCountdown()
  },
  methods: {
    onMaintenanceStatusChange(isMaintenance) {
      this.isVisible = isMaintenance
      
      if (isMaintenance) {
        this.startCountdown()
      } else {
        this.stopCountdown()
        // Wenn Maintenance-Modus beendet wird, zur richtigen Seite weiterleiten
        this.redirectToCorrectPage()
      }
    },
    
    redirectToCorrectPage() {
      // Prüfe ob User eingeloggt ist
      const token = localStorage.getItem('approvalSystemToken')
      
      if (token) {
        // User ist eingeloggt -> zur Freigabe-Seite
        this.$router.push('/freigabe')
      } else {
        // User ist nicht eingeloggt -> zur Login-Seite
        this.$router.push('/login')
      }
    },
    
    startCountdown() {
      this.stopCountdown() // Stoppe eventuell laufenden Countdown
      this.countdown = 30 // Reset auf 30 Sekunden
      
      this.countdownInterval = setInterval(() => {
        this.countdown--
        
        if (this.countdown <= 0) {
          this.countdown = 30 // Reset für nächsten Zyklus
        }
      }, 1000)
    },
    
    stopCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
        this.countdownInterval = null
      }
    },
    
    async retryConnection() {
      this.isRetrying = true
      
      try {
        // Warte kurz, damit der Benutzer die Animation sieht
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        // Prüfe den Status erneut
        await maintenanceService.forceCheck()
        
        // Countdown zurücksetzen nach manueller Prüfung
        this.countdown = 30
        
        // Wenn Maintenance-Modus beendet wurde, zur richtigen Seite weiterleiten
        if (!maintenanceService.getMaintenanceMode()) {
          this.redirectToCorrectPage()
        }
      } catch (error) {
        console.error('Retry failed:', error)
      } finally {
        this.isRetrying = false
      }
    }
  }
}
</script>

<style scoped>
.maintenance-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(5px);
}

.maintenance-modal {
  background: white;
  border-radius: 20px;
  padding: 40px;
  max-width: 500px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideIn 0.5s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-50px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.maintenance-icon {
  font-size: 4rem;
  color: #42b983;
  margin-bottom: 20px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.maintenance-title {
  color: #2c3e50;
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 20px;
  margin-top: 0;
}

.maintenance-message {
  color: #555;
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 15px;
}

.maintenance-submessage {
  color: #777;
  font-size: 1rem;
  margin-bottom: 30px;
}

.loading-animation {
  margin: 30px 0;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #42b983;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.dots {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.dots span {
  width: 12px;
  height: 12px;
  background: #42b983;
  border-radius: 50%;
  animation: bounce 1.4s ease-in-out infinite both;
}

.dots span:nth-child(1) { animation-delay: -0.32s; }
.dots span:nth-child(2) { animation-delay: -0.16s; }

@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

.retry-section {
  margin-top: 30px;
}

.countdown-section {
  margin-top: 20px;
}

.countdown-text {
  color: #777;
  font-size: 0.9rem;
  margin: 0;
}

.countdown-timer {
  color: #42b983;
  font-weight: bold;
  font-size: 1rem;
}

.retry-button {
  background: linear-gradient(135deg, #42b983, #3aa876);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.retry-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(66, 185, 131, 0.4);
}

.retry-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.spinning {
  animation: spin 1s linear infinite;
}

/* Responsive Design */
@media (max-width: 600px) {
  .maintenance-modal {
    padding: 30px 20px;
    margin: 20px;
  }
  
  .maintenance-title {
    font-size: 1.5rem;
  }
  
  .maintenance-message {
    font-size: 1rem;
  }
  
  .maintenance-icon {
    font-size: 3rem;
  }
}
</style>
